import { computed, onMounted, onUnmounted, unref, watch, type MaybeRef } from 'vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { tryoutContextAPI } from '@/api'
import { appSocket } from '@/services/socket'

export type TryoutContextDomain =
  'attendance' |
  'attributes' |
  'comments' |
  'events' |
  'feedbackSelections' |
  'participants' |
  'scores' |
  'sessions' |
  'teamAllocations' |
  string

export interface TryoutContextChangedPayload {
  type?: string
  domain?: TryoutContextDomain
  clubId: string
  seasonId: string
  tryoutId: string
  eventId?: string
  resourceId?: string | null
  participantId?: string | null
  sessionId?: string
  operation?: 'created' | 'updated' | 'deleted' | 'reordered' | 'completed' | 'reset' | string
  updatedAt?: string
  source?: string
  metadata?: Record<string, unknown>
}

const INVALIDATION_DEBOUNCE_MS = 150
const pendingInvalidations = new Map<string, number>()

export const tryoutContextQueryKey = (clubId: string, seasonId: string, tryoutId: string) => [
  'tryoutContext',
  clubId,
  seasonId,
  tryoutId,
]

export const tryoutParticipantsQueryKey = (clubId: string, seasonId: string, tryoutId: string) => [
  'tryoutParticipants',
  clubId,
  seasonId,
  tryoutId,
]

export const tryoutCaptureQueryKey = (clubId: string, seasonId: string, tryoutId: string) => [
  'tryoutCapture',
  clubId,
  seasonId,
  tryoutId,
]

export function useTryoutContext(
  clubId: MaybeRef<string>,
  seasonId: MaybeRef<string>,
  tryoutId: MaybeRef<string>
) {
  return useQuery({
    queryKey: computed(() => tryoutContextQueryKey(unref(clubId), unref(seasonId), unref(tryoutId))),
    queryFn: ({ signal }) => tryoutContextAPI.loadContext(unref(seasonId), unref(tryoutId), unref(clubId), signal),
    enabled: computed(() => Boolean(unref(clubId) && unref(seasonId) && unref(tryoutId))),
  })
}

export function useTryoutSessionInvalidation(
  clubId: MaybeRef<string>,
  seasonId: MaybeRef<string>,
  tryoutId: MaybeRef<string>
) {
  const queryClient = useQueryClient()
  const room = computed(() => unref(tryoutId) ? `tryout-context-${unref(tryoutId)}` : '')

  const invalidate = (queryKey: unknown[]) => {
    const key = JSON.stringify(queryKey)
    const existingTimer = pendingInvalidations.get(key)
    if (existingTimer) window.clearTimeout(existingTimer)

    pendingInvalidations.set(key, window.setTimeout(() => {
      pendingInvalidations.delete(key)
      queryClient.invalidateQueries({ queryKey })
    }, INVALIDATION_DEBOUNCE_MS))
  }

  const invalidateParticipantDetail = (participantId: string | null | undefined) => {
    if (!participantId) return
    invalidate(['tryoutParticipant', unref(clubId), unref(seasonId), unref(tryoutId), participantId])
  }

  const handleContextChanged = (payload: TryoutContextChangedPayload) => {
    if (!matchesCurrentTryout(payload, unref(clubId), unref(seasonId), unref(tryoutId))) return
    invalidate(['programSettings', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['programEpisodes', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['journalWorkspace', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['journalSurveys', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['tryoutHome', unref(seasonId), unref(tryoutId), unref(clubId)])
    invalidate(['athleteJournal', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['journalRewards', unref(clubId), unref(seasonId), unref(tryoutId)])
    invalidate(['journalRewardSettings', unref(clubId), unref(seasonId), unref(tryoutId)])

    const currentClubId = unref(clubId)
    const currentSeasonId = unref(seasonId)
    const currentTryoutId = unref(tryoutId)

    switch (payload.domain) {
      case 'sessions':
        invalidate(tryoutContextQueryKey(currentClubId, currentSeasonId, currentTryoutId))
        invalidate(['tryoutHome', currentSeasonId, currentTryoutId, currentClubId || ''])
        invalidate(tryoutParticipantsQueryKey(currentClubId, currentSeasonId, currentTryoutId))
        invalidate(['tryoutSetupSessions', currentSeasonId, currentTryoutId])
        invalidate(tryoutCaptureQueryKey(currentClubId, currentSeasonId, currentTryoutId))
        invalidate(['playerSkillAggregates', currentClubId, currentSeasonId, currentTryoutId])
        break

      case 'events':
      case 'attributes':
        invalidate(['tryoutSetupStaff', currentSeasonId, currentTryoutId])
        invalidate(['tryoutSetupRubrics', currentSeasonId, currentTryoutId])
        invalidate(['tryoutSetupRubric', currentSeasonId, currentTryoutId])
        invalidate(['seasonEvents', currentClubId, currentSeasonId])
        invalidate(tryoutContextQueryKey(currentClubId, currentSeasonId, currentTryoutId))
        invalidate(['tryoutHome', currentSeasonId, currentTryoutId, currentClubId || ''])
        invalidate(tryoutParticipantsQueryKey(currentClubId, currentSeasonId, currentTryoutId))
        invalidate(tryoutCaptureQueryKey(currentClubId, currentSeasonId, currentTryoutId))
        invalidate(['playerSkillAggregates', currentClubId, currentSeasonId, currentTryoutId])
        break

      case 'participants':
        invalidate(['tryoutHome', currentSeasonId, currentTryoutId, currentClubId || ''])
        invalidate(tryoutParticipantsQueryKey(currentClubId, currentSeasonId, currentTryoutId))
        invalidateParticipantDetail(payload.participantId)
        invalidate(['tryoutTeamAllocations', currentClubId, currentSeasonId, currentTryoutId])
        invalidate(['tryoutRecommendations', currentClubId, currentSeasonId, currentTryoutId])
        break

      case 'attendance':
      case 'comments':
        invalidate(tryoutParticipantsQueryKey(currentClubId, currentSeasonId, currentTryoutId))
        invalidateParticipantDetail(payload.participantId)
        invalidate(tryoutCaptureQueryKey(currentClubId, currentSeasonId, currentTryoutId))
        invalidate(['playerSkillAggregates', currentClubId, currentSeasonId, currentTryoutId])
        break

      case 'scores':
      case 'feedbackSelections':
        invalidate(tryoutParticipantsQueryKey(currentClubId, currentSeasonId, currentTryoutId))
        invalidate(['playerSkillAggregates', currentClubId, currentSeasonId, currentTryoutId])
        invalidate(tryoutCaptureQueryKey(currentClubId, currentSeasonId, currentTryoutId))
        invalidate(['tryoutRecommendations', currentClubId, currentSeasonId, currentTryoutId])
        invalidate(['tryoutTeamAllocations', currentClubId, currentSeasonId, currentTryoutId])
        break

      case 'teamAllocations':
        invalidate(tryoutParticipantsQueryKey(currentClubId, currentSeasonId, currentTryoutId))
        invalidate(['tryoutTeamAllocations', currentClubId, currentSeasonId, currentTryoutId])
        invalidate(['tryoutRecommendations', currentClubId, currentSeasonId, currentTryoutId])
        break
    }
  }

  const stopRoomWatch = watch(room, (nextRoom, previousRoom) => {
    if (previousRoom) appSocket.leaveRoom(previousRoom)
    if (nextRoom) appSocket.joinRoom(nextRoom)
  }, { immediate: true })

  onMounted(() => {
    appSocket.on('tryout.context.changed', handleContextChanged)
    appSocket.on('tryout.scores.changed', handleScoresChanged)
  })

  onUnmounted(() => {
    stopRoomWatch()
    if (room.value) appSocket.leaveRoom(room.value)
    appSocket.off('tryout.context.changed', handleContextChanged)
    appSocket.off('tryout.scores.changed', handleScoresChanged)
  })

  function handleScoresChanged(payload: TryoutContextChangedPayload) {
    if (payload.domain !== 'scores') return
    if (!matchesCurrentTryout(payload, unref(clubId), unref(seasonId), unref(tryoutId))) return
    invalidate(tryoutCaptureQueryKey(unref(clubId), unref(seasonId), unref(tryoutId)))
  }
}

function matchesCurrentTryout(
  payload: TryoutContextChangedPayload,
  clubId: string,
  seasonId: string,
  tryoutId: string
) {
  return String(payload.clubId || '') === clubId &&
    String(payload.seasonId || '') === seasonId &&
    String(payload.tryoutId || '') === tryoutId
}
